import { useMemo } from 'react';
import * as d3 from 'd3';
import { nodeHorizontalLayout, nodeVerticalLayout } from './layout';
import { BusinessJourneyLayout, HierarchyData } from '../model';
import { calculateCanvasSize } from '../utils';

type ComputeLayoutProps<DT extends HierarchyData> = {
  layout: BusinessJourneyLayout;
  nodesData: DT[];
  nodeWidth: number;
  nodeHeight: number;
};

type ComputeLayoutResult<DT extends HierarchyData> = {
  root: d3.HierarchyPointNode<DT>;
  canvasWidth: number;
  canvasHeight: number;
};

export function useComputeLayout<DT extends HierarchyData>({
  layout,
  nodesData,
  nodeWidth,
  nodeHeight,
}: ComputeLayoutProps<DT>): ComputeLayoutResult<DT> {
  const hierarchyRootNode = useMemo(
    () =>
      d3
        .stratify<DT>()
        .id(d => d.id.toString())
        .parentId(d => d.parentId?.toString())(nodesData),
    [nodesData],
  );

  return useMemo(() => {
    const root: d3.HierarchyPointNode<DT> = d3.tree<DT>().nodeSize([1, 1])(hierarchyRootNode);
    if (layout === 'horizontal') {
      nodeHorizontalLayout(root, nodeWidth);
    } else {
      nodeVerticalLayout(root, nodeHeight);
    }
    const { canvasWidth, canvasHeight } = calculateCanvasSize(root, nodeWidth, nodeHeight);
    return { root, canvasWidth, canvasHeight };
  }, [layout, hierarchyRootNode, nodeWidth, nodeHeight]);
}
